import { useEffect, useMemo, useState } from "react";
import { useI18n } from "@/i18n/I18nContext";
import { pickLocalized } from "@/i18n/translations";
import Ornament from "@/components/Ornament";
import api from "@/lib/api";

const MANUSCRIPT = "/photos/prieure-leaves.jpg";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const resolveUrl = (u) => {
  if (!u) return "#";
  if (u.startsWith("http")) return u;
  return `${BACKEND}${u}`;
};

const FormatIcon = ({ format }) => {
  const color = "var(--cinnabar)";
  const props = {
    width: 22, height: 22, viewBox: "0 0 24 24", fill: "none",
    stroke: "currentColor", strokeWidth: 1.5, style: { color },
    className: "shrink-0 mt-1",
  };
  if (format === "image" || ["jpg","jpeg","png","webp","gif","svg","heic"].includes(format)) {
    return (
      <svg {...props}>
        <rect x="3" y="4" width="18" height="16" rx="1" />
        <circle cx="9" cy="10" r="1.5" />
        <path d="m21 16-5-5L7 20" />
      </svg>
    );
  }
  if (format === "audio" || ["mp3","wav","ogg","m4a","aac","flac"].includes(format)) {
    return (
      <svg {...props}>
        <path d="M9 18V5l12-2v13" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="18" cy="16" r="3" />
      </svg>
    );
  }
  if (format === "video" || ["mp4","mov","webm","mkv"].includes(format)) {
    return (
      <svg {...props}>
        <rect x="2" y="6" width="14" height="12" rx="1" />
        <path d="m16 10 6-3v10l-6-3z" />
      </svg>
    );
  }
  // default: page/document icon
  return (
    <svg {...props}>
      <path d="M14 3 H7 a2 2 0 0 0 -2 2 v14 a2 2 0 0 0 2 2 h10 a2 2 0 0 0 2 -2 V8 Z M14 3 v5 h5" />
    </svg>
  );
};

const formatSize = (bytes) => {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

export default function Documents() {
  const { t, locale } = useI18n();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  // Progressive rendering: render in chunks of 20 to keep first paint snappy
  const PAGE_SIZE = 20;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    let mounted = true;
    api.get("/documents")
      .then((r) => {
        if (!mounted) return;
        setItems(r.data.items || []);
        setCategories(r.data.categories || []);
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  // Reset visible window when filter or search changes
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [filter, query]);

  const labels = t.documents.categories;
  const formatLabel = (f) => t.documents.formats[f] || f.toUpperCase();

  const trackAndOpen = (it) => {
    // Fire-and-forget download tracking, then open the link
    api.post(`/documents/${it.id}/track-download`).catch(() => {});
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (filter !== "all" && it.category !== filter) return false;
      if (!q) return true;
      const title = pickLocalized(it.title, locale).toLowerCase();
      const desc = pickLocalized(it.description || {}, locale).toLowerCase();
      return title.includes(q) || desc.includes(q);
    });
  }, [items, filter, query, locale]);

  // Featured section : only shown when no filter / no query is applied
  const featured = useMemo(() => {
    if (filter !== "all" || query.trim()) return [];
    return items.filter((it) => it.featured);
  }, [items, filter, query]);

  const grouped = useMemo(() => {
    // Limit to visibleCount items overall, preserving category grouping order
    const limited = filtered.slice(0, visibleCount);
    const map = {};
    limited.forEach((it) => {
      (map[it.category] = map[it.category] || []).push(it);
    });
    return map;
  }, [filtered, visibleCount]);

  const hasMore = filtered.length > visibleCount;

  const DocCard = ({ it, highlight = false }) => {
    const title = pickLocalized(it.title, locale);
    const desc = pickLocalized(it.description || {}, locale);
    const count = it.download_count || 0;
    return (
      <a
        href={resolveUrl(it.file_url)}
        target="_blank"
        rel="noreferrer"
        onClick={() => trackAndOpen(it)}
        data-testid={`doc-item-${it.id}`}
        className="flex items-start gap-4 p-6 border bg-white hover:bg-[var(--parchment-2)] transition-colors group"
        style={{
          borderColor: highlight ? "var(--gold)" : "var(--border)",
          borderWidth: highlight ? 2 : 1,
        }}
      >
        <FormatIcon format={it.format} />
        <div className="flex-1 min-w-0">
          {highlight && (
            <div className="font-caps text-[0.6rem] mb-1" style={{ color: "var(--gold)", letterSpacing: "0.15em" }}>
              ★ {t.documents.featured}
            </div>
          )}
          <div className="font-body text-lg leading-snug">{title}</div>
          {desc && (
            <div className="font-body text-sm mt-1" style={{ color: "var(--muted)" }}>
              {desc}
            </div>
          )}
          <div className="flex items-center gap-3 mt-3 font-caps text-[0.65rem]" style={{ color: "var(--muted)" }}>
            <span>{formatLabel(it.format)}</span>
            {it.size > 0 && <span>· {formatSize(it.size)}</span>}
            {count > 0 && (
              <span data-testid={`doc-count-${it.id}`}>
                · {count} {count === 1 ? t.documents.downloadsOne : t.documents.downloads}
              </span>
            )}
            <span className="ml-auto group-hover:underline" style={{ color: "var(--cinnabar)" }}>
              {t.documents.open} →
            </span>
          </div>
        </div>
      </a>
    );
  };

  return (
    <div data-testid="documents-page">
      <section className="relative" style={{ height: "42vh", minHeight: 320 }}>
        <img src={MANUSCRIPT} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="container-prieure relative h-full flex flex-col justify-end pb-12">
          <div className="font-caps text-[0.72rem] text-white/90 mb-4">{t.documents.kicker}</div>
          <h1 className="font-display text-5xl sm:text-7xl text-white">{t.documents.title}</h1>
        </div>
      </section>

      <section className="py-16">
        <div className="container-prieure max-w-4xl">
          <Ornament className="mb-6" />
          <p className="font-display italic text-2xl text-center" style={{ color: "var(--ink-2)" }}>
            {t.documents.intro}
          </p>
        </div>
      </section>

      <section className="pb-32">
        <div className="container-prieure space-y-10">

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3" data-testid="doc-filters">
            <button
              data-testid="doc-filter-all"
              onClick={() => setFilter("all")}
              className="px-4 py-2 border font-caps text-[0.7rem] transition-colors"
              style={{
                borderColor: "var(--border)",
                background: filter === "all" ? "var(--ink)" : "transparent",
                color: filter === "all" ? "var(--parchment)" : "var(--ink)",
              }}
            >
              {labels.all}
            </button>
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`doc-filter-${c}`}
                onClick={() => setFilter(c)}
                className="px-4 py-2 border font-caps text-[0.7rem] transition-colors"
                style={{
                  borderColor: "var(--border)",
                  background: filter === c ? "var(--ink)" : "transparent",
                  color: filter === c ? "var(--parchment)" : "var(--ink)",
                }}
              >
                {labels[c] || c}
              </button>
            ))}
            <div className="flex-1 min-w-[200px]">
              <input
                data-testid="doc-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.documents.searchPlaceholder}
                className="input-classic w-full"
              />
            </div>
          </div>

          {loading && (
            <div className="text-center font-display italic text-xl" style={{ color: "var(--muted)" }}>…</div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-10 font-display italic text-xl" style={{ color: "var(--muted)" }} data-testid="doc-empty">
              {t.documents.empty}
            </div>
          )}

          {/* Featured section (only when no filter / no search) */}
          {!loading && featured.length > 0 && (
            <div data-testid="doc-featured">
              <div className="deco-rule mb-6">
                <span className="font-caps text-[0.78rem]" style={{ color: "var(--gold)", letterSpacing: "0.15em" }}>
                  ★ {t.documents.featured}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {featured.map((it) => <DocCard key={`f-${it.id}`} it={it} highlight />)}
              </div>
            </div>
          )}

          {/* Grouped list */}
          {!loading && categories.map((cat) => {
            const list = grouped[cat] || [];
            if (list.length === 0) return null;
            return (
              <div key={cat} data-testid={`doc-section-${cat}`}>
                <div className="deco-rule mb-6">
                  <span className="font-caps text-[0.78rem]" style={{ color: "var(--ink)" }}>
                    {labels[cat] || cat}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {list.map((it) => <DocCard key={it.id} it={it} />)}
                </div>
              </div>
            );
          })}

          {/* Progressive 'Load more' — keeps first paint fast for very long lists */}
          {!loading && hasMore && (
            <div className="text-center pt-6" data-testid="doc-load-more-wrap">
              <button
                data-testid="doc-load-more"
                onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                className="px-6 py-3 border font-caps text-[0.7rem]"
                style={{
                  borderColor: "var(--ink)", color: "var(--ink)",
                  letterSpacing: "0.22em", background: "transparent",
                }}
              >
                {locale === "fr" ? "↓ Charger plus" : "↓ Load more"}
                <span className="ml-2" style={{ color: "var(--muted)" }}>
                  ({visibleCount} / {filtered.length})
                </span>
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
